import { useEffect, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { dismissSplash } from "../../utils/splash";
import { pendingPageCount, subscribePendingPage } from "../../utils/pendingPage";

/**
 * The single place that decides WHEN the branded boot splash goes away.
 *
 * The splash node itself (#splash, a sibling of #root) is written during HTML
 * parse — see public/index.html. Mount this once, high in App, and every route
 * gets the splash for free: the node lives outside the router, so no page needs
 * to know it exists.
 *
 * Dismissal rule — first of:
 *
 *  1. `salonLoading === false`, checked one animation frame after mount. One
 *     frame is enough for route-level useEffect dispatches to have landed, so
 *     this single condition covers BOTH "the business has resolved" and "this
 *     route never fetches a business at all". The second half matters: only
 *     Home, CustomerHome, AddAppointment, Gallery and BookedSuccessfull ever
 *     dispatch getSalonSpecificInfo — waiting on salon data alone would leave
 *     /shop, /checkout, /p/:id, /stays and /orders/:id stuck behind the splash
 *     with real content rendered underneath.
 *
 *     A page that is its own chunk (utils/lazyPage.js) has not mounted, let
 *     alone fetched its business, while the chunk is in flight — so the gate
 *     also waits until no page is pending (utils/pendingPage.js). Without
 *     that, the splash left for a blank screen on the first load of every
 *     lazy page and the page's own loader came up after it.
 *
 *  2. An 8s safety timeout, so a hung request can't trap anyone. That timeout
 *     also exists inline in index.html, which is the one that survives the JS
 *     bundle failing to load at all; this is its in-bundle counterpart.
 */
const SPLASH_TIMEOUT_MS = 8000;

const SplashGate = () => {
  const salonLoading = useSelector((state) => state.getSalonReducer?.salonLoading);
  const pendingPages = useSyncExternalStore(subscribePendingPage, pendingPageCount, pendingPageCount);

  useEffect(() => {
    const timeout = window.setTimeout(dismissSplash, SPLASH_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (salonLoading || pendingPages > 0) return undefined;
    // rAF, not a bare call: on first mount this effect runs before the route's
    // own fetch-dispatching effect has necessarily run, and dismissing there
    // would kill the splash a frame before the salon request even starts.
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(dismissSplash);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [salonLoading, pendingPages]);

  return null;
};

export default SplashGate;
